import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { decryptConversationText, ensureConversationKey, encryptConversationText, getConversationKeyRaw } from '../lib/conversation-crypto';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

type GroupMessage = { messageId: string; senderId: string; timestamp: string; content: string };

function displayRole(role: string): string {
  if (role === 'owner') return t('オーナー', 'Owner');
  if (role === 'admin') return t('管理者', 'Admin');
  if (role === 'moderator') return t('モデレーター', 'Moderator');
  if (role === 'member') return t('メンバー', 'Member');
  return role;
}

export function GroupChatPage() {
  const { circleId, groupId } = useParams<{ circleId: string; groupId: string }>();
  const [groupName, setGroupName] = useState(t('グループ', 'Group'));
  const [circleName, setCircleName] = useState('');
  const [groupMembers, setGroupMembers] = useState<Array<{ userId: string; username: string; role: string }>>([]);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviting, setInviting] = useState(false);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const [group, result, activeCircle] = await Promise.all([
        api.group.getGroup({ groupId }),
        api.group.getMessages({ groupId, limit: 100 }),
        api.circle.getActiveCircle()
      ]);
      setCircleName(activeCircle?.name ?? '');
      setGroupName(group.meta.name);
      setGroupMembers(group.meta.members.map((member) => ({ userId: member.userId, username: member.username, role: member.role })));

      const key = await ensureConversationKey('group', groupId, group.wrappedKey);

      const decrypted = await Promise.all(
        result.messages.map(async (message): Promise<GroupMessage> => {
          try {
            return {
              messageId: message.messageId,
              senderId: message.senderId,
              timestamp: message.timestamp,
              content: await decryptConversationText(message.ciphertext, key)
            };
          } catch {
            return {
              messageId: message.messageId,
              senderId: message.senderId,
              timestamp: message.timestamp,
              content: t('[復号失敗]', '[decrypt failed]')
            };
          }
        })
      );
      setMessages(decrypted);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('グループチャットの取得に失敗しました', 'Failed to load group chat'));
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async () => {
    if (!groupId || !draft.trim()) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      const group = await api.group.getGroup({ groupId });
      const key = await ensureConversationKey('group', groupId, group.wrappedKey);
      const timestamp = new Date().toISOString();
      const content = draft.trim();
      const ciphertext = await encryptConversationText(content, key);
      const result = await api.group.sendMessage({ groupId, ciphertext, timestamp });
      setMessages((previous) => [{ messageId: result.messageId, senderId: 'me', timestamp, content }, ...previous]);
      setDraft('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('メッセージ送信に失敗しました', 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const inviteMember = async () => {
    if (!groupId || !inviteUsername.trim()) {
      return;
    }

    setInviting(true);
    setError(null);
    try {
      const sharedKey = getConversationKeyRaw('group', groupId);
      if (!sharedKey) {
        throw new Error(t('ローカルにグループ鍵がありません', 'Group key not found locally'));
      }

      await api.group.addMember({
        groupId,
        username: inviteUsername.trim(),
        wrappedKey: sharedKey,
        ephemeralPk: sharedKey,
        fingerprintVerified: false
      });

      setInviteUsername('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('メンバー招待に失敗しました', 'Failed to invite member'));
    } finally {
      setInviting(false);
    }
  };

  return (
    <main className="mx-auto min-h-screen w-full max-w-2xl space-y-4 px-4 py-10">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold">{groupName}</h1>
          <p className="text-xs text-muted-foreground">{t('サークル', 'Circle')}: {circleName || t('未選択', 'Not selected')}</p>
        </div>
        <Button asChild variant="outline">
          <Link to={circleId ? `/${circleId}/groups` : '/circles'}>{t('一覧へ', 'Back to list')}</Link>
        </Button>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('メッセージ送信', 'Messages')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={inviteUsername}
              onChange={(e) => setInviteUsername(e.target.value)}
              placeholder={t('招待するユーザー名', 'Invite username')}
            />
            <Button onClick={inviteMember} disabled={inviting || !inviteUsername.trim()}>
              {inviting ? t('招待中...', 'Inviting...') : t('招待', 'Invite')}
            </Button>
          </div>

          {groupMembers.length > 0 ? (
            <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
              {groupMembers.map((member) => (
                <p key={member.userId}>{member.username} ({displayRole(member.role)})</p>
              ))}
            </div>
          ) : null}

          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-24" placeholder={t('メッセージ', 'Message')} />
          <Button className="mt-3" onClick={send} disabled={sending || !draft.trim()}>
            {sending ? t('送信中...', 'Sending...') : t('送信', 'Send')}
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((message) => (
            <Card key={message.messageId}>
              <CardContent className="space-y-1 py-3">
                <p className="text-xs text-muted-foreground">{message.senderId === 'me' ? t('自分', 'Me') : message.senderId} / {new Date(message.timestamp).toLocaleString()}</p>
                <p className="whitespace-pre-wrap text-sm">{message.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}
